import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  forwardRef,
  input,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UiInputDirective } from '../input/input.directive';

let nextCurrencyInputId = 0;

/**
 * Currency amount field: a `$` prefix over a token-styled number input.
 * Implements ControlValueAccessor, so it drops into reactive (`formControlName`)
 * and template-driven (`[(ngModel)]`) forms alike — replacing the hand-rolled
 * `relative > $ span + appUiInput[type=number]` blocks across the app.
 */
@Component({
  selector: 'app-ui-currency-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiInputDirective],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => UiCurrencyInputComponent), multi: true },
  ],
  template: `
    <div class="relative">
      <span
        class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-text-secondary"
        aria-hidden="true"
        >$</span
      >
      <input
        appUiInput
        type="number"
        class="!pl-9 tabular-nums"
        [id]="inputId()"
        [attr.name]="name()"
        [attr.min]="min()"
        [attr.max]="max()"
        [step]="step()"
        [placeholder]="placeholder()"
        [required]="required()"
        [disabled]="isDisabled()"
        [attr.data-testid]="testId()"
        [attr.aria-label]="ariaLabel()"
        [attr.aria-describedby]="ariaDescribedBy()"
        [value]="value()"
        (input)="handleInput($event)"
        (focus)="focused.emit()"
        (focusout)="handleFocusout()"
      />
    </div>
  `,
})
export class UiCurrencyInputComponent implements ControlValueAccessor {
  readonly inputId = input<string>(`app-ui-currency-input-${nextCurrencyInputId++}`);
  readonly name = input<string | null>(null);
  readonly min = input<number | string | null>(0);
  readonly max = input<number | string | null>(null);
  readonly step = input<number | string>(0.01);
  readonly placeholder = input<string>('');
  readonly required = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly testId = input<string | null>(null);
  readonly ariaLabel = input<string | null>(null);
  readonly ariaDescribedBy = input<string | null>(null);

  readonly focused = output<void>();
  readonly blurred = output<void>();

  protected readonly value = signal<number | null>(null);
  /** Disabled state pushed by reactive forms via setDisabledState. */
  protected readonly cvaDisabled = signal(false);
  /** Effective disabled = template-driven input OR reactive-forms state. */
  protected readonly isDisabled = computed(() => this.disabled() || this.cvaDisabled());

  private onChange: (value: number | null) => void = () => undefined;
  protected onTouched: () => void = () => undefined;

  handleInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).valueAsNumber;
    const next = Number.isNaN(raw) ? null : raw;
    this.value.set(next);
    this.onChange(next);
  }

  handleFocusout(): void {
    this.onTouched();
    this.blurred.emit();
  }

  writeValue(value: number | null): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
  }
}
